import React, { useState, useEffect, useRef } from "react";
import api from "../../../services/api";
import snakeData from "./snake_data.json";
import { useSelector } from "react-redux";

export function Snake() {
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user, setUser } = useSelector((state) => state.Auth);
  const [snake, setSnake] = useState([{ x: 10, y: 10 }]);
  const [food, setFood] = useState({ x: 5, y: 5 });
  const [direction, setDirection] = useState("RIGHT");
  const [cheatMode, setCheatMode] = useState(false);
  const [speed, setSpeed] = useState(110);
  const [gameOver, setGameOver] = useState(false);
  const [score, setScore] = useState(0);
  const [avatars, setAvatars] = useState([]);
  const [foodAvatar, setFoodAvatar] = useState(null);
  const [particles, setParticles] = useState([]);
  const [level, setLevel] = useState(1);
  const [gameStarted, setGameStarted] = useState(false);
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const gridSize = 40;
  const canvasSize = 600;

  const fetchLeaderboard = async () => {
    try {
      setLoading(true);
      const { ok, data } = await api.post("/user/leaderboard");

      if (!ok) throw new Error("Error fetching leaderboard");

      const topPlayers = data.filter((user) => user.snake_record > 0);
      setLeaderboard(topPlayers);
    } catch (error) {
      console.error("Error fetching leaderboard:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeaderboard();
  }, []);

  useEffect(() => {
    const loadImages = async () => {
      const sebastien = snakeData.find((person) => person.name === "Sebastien Le Goff");
      const sebastienAvatar = sebastien ? new Image() : null;
      if (sebastienAvatar) {
        sebastienAvatar.src = sebastien.avatar;
        sebastienAvatar.onload = () => setFoodAvatar(sebastienAvatar);
      }

      const otherAvatars = snakeData.filter((person) => person.name !== "Sebastien Le Goff");
      const loadedAvatars = await Promise.all(
        otherAvatars.map((person) => {
          return new Promise((resolve) => {
            const img = new Image();
            img.src = person.avatar;
            img.onload = () => resolve(img);
            img.onerror = () => {
              const fallbackImg = new Image();
              fallbackImg.src = "https://via.placeholder.com/40";
              resolve(fallbackImg);
            };
          });
        }),
      );
      setAvatars(loadedAvatars);
    };

    loadImages();
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      switch (e.key) {
        case "ArrowUp":
          if (direction !== "DOWN") setDirection("UP");
          break;
        case "ArrowDown":
          if (direction !== "UP") setDirection("DOWN");
          break;
        case "ArrowLeft":
          if (direction !== "RIGHT") setDirection("LEFT");
          break;
        case "ArrowRight":
          if (direction !== "LEFT") setDirection("RIGHT");
          break;
        case "c":
          setCheatMode(!cheatMode);
          setDirection("RIGHT");
          break;
        default:
          break;
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [direction]);

  useEffect(() => {
    if (gameOver || !gameStarted) return;

    const gameInterval = setInterval(updateGame, speed);
    return () => clearInterval(gameInterval);
  }, [snake, food, direction, gameOver, gameStarted]);

  useEffect(() => {
    drawGame();

    // Handle particle animation
    const updateParticles = () => {
      setParticles((prevParticles) =>
        prevParticles
          .map((p) => ({
            ...p,
            life: p.life - 1,
            x: p.x + p.vx,
            y: p.y + p.vy,
            size: p.size * 0.95,
          }))
          .filter((p) => p.life > 0),
      );
      animationRef.current = requestAnimationFrame(updateParticles);
    };

    animationRef.current = requestAnimationFrame(updateParticles);
    return () => cancelAnimationFrame(animationRef.current);
  }, [snake, food, gameOver, avatars, foodAvatar, particles, gameStarted]);

  // Save user score if it's a new record
  const saveScore = async () => {
    if (score > user.snake_record) {
      try {
        await api.put(`/user/${user._id}`, { snake_record: score });
        setUser({ ...user, snake_record: score });
      } catch (error) {
        console.error("Error saving score:", error);
      }
    }
  };

  // Create particles when eating food
  const createFoodParticles = (x, y) => {
    const newParticles = [];
    const colors = ["#FF5722", "#FFC107", "#FFEB3B", "#4CAF50"];

    for (let i = 0; i < 20; i++) {
      newParticles.push({
        x: x * gridSize + gridSize / 2,
        y: y * gridSize + gridSize / 2,
        vx: (Math.random() - 0.5) * 4,
        vy: (Math.random() - 0.5) * 4,
        size: Math.random() * 8 + 2,
        color: colors[Math.floor(Math.random() * colors.length)],
        life: 30 + Math.floor(Math.random() * 20),
      });
    }

    setParticles((prev) => [...prev, ...newParticles]);
  };

  const updateGame = () => {
    const newSnake = [...snake];
    let head = { ...newSnake[0] };
    const gridWidth = canvasSize / gridSize;
    const gridHeight = canvasSize / gridSize;

    if (cheatMode) {
      if (head.x === gridWidth - 1 && direction === "RIGHT") {
        setDirection("DOWN");
        head.y = head.y + 1;
      } else if (head.x === gridWidth - 1 && direction === "DOWN") {
        head.x = head.x - 1;
        setDirection("LEFT");
      } else if (head.x === 0 && direction === "LEFT") {
        head.y = head.y + 1;
        setDirection("DOWN");
      } else if (head.x === 0 && direction === "DOWN") {
        head.x = head.x + 1;
        setDirection("RIGHT");
      } else if (direction === "RIGHT") {
        head.x = head.x + 1;
      } else if (direction === "LEFT") {
        head.x = head.x - 1;
      }
    } else {
      switch (direction) {
        case "RIGHT":
          head.x = head.x + 1;
          break;
        case "LEFT":
          head.x = head.x - 1;
          break;
        case "UP":
          head.y = head.y - 1;
          break;
        case "DOWN":
          head.y = head.y + 1;
          break;
        default:
          break;
      }
    }

    // Implement wrap-around behavior
    if (head.x < 0) head.x = gridWidth - 1;
    if (head.x >= gridWidth) head.x = 0;
    if (head.y < 0) head.y = gridHeight - 1;
    if (head.y >= gridHeight) head.y = 0;

    // Only check for collision with self
    if (checkCollision(head)) {
      setGameOver(true);
      saveScore();
      return;
    }

    newSnake.unshift(head);

    // Check for food collision
    if (head.x === food.x && head.y === food.y) {
      setScore(score + level * 10);
      createFoodParticles(food.x, food.y);
      generateFood();

      // Level up when snake grows
      if (newSnake.length % 5 === 0) {
        setLevel((prevLevel) => prevLevel + 1);
        setSpeed((prevSpeed) => Math.max(50, prevSpeed - 5));
      }
    } else {
      newSnake.pop();
    }

    setSnake(newSnake);
  };

  const checkCollision = (head) => {
    for (let i = 1; i < snake.length; i++) {
      if (head.x === snake[i].x && head.y === snake[i].y) {
        return true;
      }
    }
    return false;
  };

  const generateFood = () => {
    const newFood = {
      x: Math.floor(Math.random() * (canvasSize / gridSize)),
      y: Math.floor(Math.random() * (canvasSize / gridSize)),
    };

    // Make sure food doesn't spawn on snake
    const isOnSnake = snake.some((segment) => segment.x === newFood.x && segment.y === newFood.y);
    if (isOnSnake) {
      generateFood();
    } else {
      setFood(newFood);
    }
  };

  const drawGame = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvasSize, canvasSize);

    ctx.fillStyle = "#f5f5f5";
    ctx.fillRect(0, 0, canvasSize, canvasSize);

    ctx.strokeStyle = "rgba(0, 0, 0, 0.1)";
    ctx.lineWidth = 1;

    // Draw grid lines
    for (let i = 0; i <= canvasSize; i += gridSize) {
      ctx.beginPath();
      ctx.moveTo(i, 0);
      ctx.lineTo(i, canvasSize);
      ctx.stroke();

      ctx.beginPath();
      ctx.moveTo(0, i);
      ctx.lineTo(canvasSize, i);
      ctx.stroke();
    }

    // Draw snake with glow effect for head
    snake.forEach((segment, index) => {
      if (index === 0) {
        // Add glow effect to snake head
        ctx.shadowColor = "#4CAF50";
        ctx.shadowBlur = 15;
      }

      if (avatars.length > 0) {
        const avatarIndex = index % avatars.length;
        ctx.drawImage(avatars[avatarIndex], segment.x * gridSize, segment.y * gridSize, gridSize, gridSize);
      } else {
        ctx.fillStyle = index === 0 ? "#4CAF50" : "#8BC34A";
        ctx.fillRect(segment.x * gridSize, segment.y * gridSize, gridSize, gridSize);
      }

      // Reset shadow for other elements
      if (index === 0) {
        ctx.shadowBlur = 0;
      }

      ctx.strokeStyle = "#388E3C";
      ctx.strokeRect(segment.x * gridSize, segment.y * gridSize, gridSize, gridSize);
    });

    // Draw food with pulsating effect
    const pulseSize = 1 + 0.1 * Math.sin(Date.now() / 200);

    if (foodAvatar) {
      ctx.save();
      ctx.translate(food.x * gridSize + gridSize / 2, food.y * gridSize + gridSize / 2);
      ctx.scale(pulseSize, pulseSize);
      ctx.drawImage(foodAvatar, -gridSize / 2, -gridSize / 2, gridSize, gridSize);
      ctx.restore();
    } else {
      ctx.fillStyle = "#FF5722";
      ctx.beginPath();
      ctx.arc(food.x * gridSize + gridSize / 2, food.y * gridSize + gridSize / 2, (gridSize / 2) * pulseSize, 0, 2 * Math.PI);
      ctx.fill();
    }

    // Draw particles
    particles.forEach((p) => {
      ctx.globalAlpha = p.life / 50;
      ctx.fillStyle = p.color;
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.size, 0, 2 * Math.PI);
      ctx.fill();
    });
    ctx.globalAlpha = 1;

    // Draw game start screen
    if (!gameStarted && !gameOver) {
      ctx.fillStyle = "rgba(0, 0, 0, 0.7)";
      ctx.fillRect(0, 0, canvasSize, canvasSize);
      ctx.font = "36px Arial";
      ctx.fillStyle = "white";
      ctx.textAlign = "center";
      ctx.fillText("SNAKE GAME", canvasSize / 2, canvasSize / 2 - 50);
      ctx.font = "20px Arial";
      ctx.fillText("Use arrow keys to control", canvasSize / 2, canvasSize / 2);
      ctx.font = "24px Arial";
      ctx.fillStyle = "#4CAF50";
      ctx.fillText("Click to Start", canvasSize / 2, canvasSize / 2 + 80);
    }

    // Draw game over screen
    if (gameOver) {
      ctx.fillStyle = "rgba(0, 0, 0, 0.8)";
      ctx.fillRect(0, 0, canvasSize, canvasSize);

      // Glowing text
      ctx.shadowColor = "red";
      ctx.shadowBlur = 15;
      ctx.font = "40px Arial";
      ctx.fillStyle = "white";
      ctx.textAlign = "center";
      ctx.fillText("Game Over!", canvasSize / 2, canvasSize / 2 - 40);
      ctx.shadowBlur = 0;

      ctx.font = "30px Arial";
      ctx.fillText(`Score: ${score}`, canvasSize / 2, canvasSize / 2 + 10);
      ctx.fillText(`Level: ${level}`, canvasSize / 2, canvasSize / 2 + 50);

      // Show if it's a new record
      if (score > user.snake_record && user.snake_record > 0) {
        ctx.fillStyle = "#FFC107";
        ctx.fillText("New Record!", canvasSize / 2, canvasSize / 2 - 90);
      }

      ctx.font = "22px Arial";
      ctx.fillStyle = "white";
      ctx.fillText("Press Space to Restart", canvasSize / 2, canvasSize / 2 + 90);
    }
  };

  const resetGame = () => {
    setSnake([{ x: 10, y: 10 }]);
    setFood({ x: 5, y: 5 });
    setDirection("RIGHT");
    setGameOver(false);
    setScore(0);
    setLevel(1);
    setSpeed(100);
    setParticles([]);
    setGameStarted(true);
  };

  const handleKeyPress = (e) => {
    if (e.key === " " && gameOver) {
      resetGame();
    }
  };

  const startGame = () => {
    if (!gameStarted && !gameOver) {
      setGameStarted(true);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center p-4">
      <div className="flex justify-between w-full max-w-[600px] mb-4">
        <div className="text-xl font-bold">Score: {score}</div>
        <div className="text-xl font-bold">Level: {level}</div>
        <div className="text-xl font-bold">Best: {user.snake_record}</div>
      </div>
      <div className="w-full flex items-start justify-around gap-4">
        <div className="relative border border-gray-300 shadow-lg rounded-lg overflow-hidden">
          <canvas ref={canvasRef} width={canvasSize} height={canvasSize} className="bg-gray-100" tabIndex="0" onKeyDown={handleKeyPress} onClick={startGame} />
          {gameOver && (
            <div className="absolute flex flex-col items-center left-1/2 transform -translate-x-1/2 bottom-20">
              <button className="px-6 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 transition duration-200 shadow-lg mb-3" onClick={resetGame}>
                Play Again
              </button>
            </div>
          )}
        </div>
        <Leaderboard leaderboard={leaderboard} setLeaderboard={setLeaderboard} loading={loading} refetch={fetchLeaderboard} />
      </div>
    </div>
  );
}

const Leaderboard = ({ leaderboard, loading, refetch }) => {
  return (
    <div className="p-6 rounded-lg w-2/5 border border-gray-100 h-[600px] overflow-y-auto">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-green-600 to-green-400">🏆 Leaderboard</h2>
        <button
          className="px-4 py-2 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg hover:from-green-600 hover:to-green-700 transition-all duration-200 shadow-lg transform hover:scale-105 active:scale-95 flex items-center gap-2"
          onClick={refetch}>
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
          Refresh
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center p-8">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-green-500 border-t-transparent"></div>
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-gray-100">
          {leaderboard.length === 0 ? (
            <div className="text-center py-8 px-4">
              <div className="text-6xl mb-4">🎮</div>
              <p className="text-gray-600 font-medium">No scores recorded yet</p>
              <p className="text-gray-500 text-sm mt-2">Be the first to make history!</p>
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="bg-gradient-to-r from-green-50 to-green-100">
                  <th className="py-3 px-4 text-left text-sm font-semibold text-gray-600">Rank</th>
                  <th className="py-3 px-4 text-left text-sm font-semibold text-gray-600">Player</th>
                  <th className="py-3 px-4 text-right text-sm font-semibold text-gray-600">Score</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {leaderboard.map((user, index) => (
                  <tr
                    key={user._id}
                    className={`transform transition-all duration-200 hover:bg-green-50 ${
                      index === 0
                        ? "bg-gradient-to-r from-yellow-50 to-yellow-100"
                        : index === 1
                        ? "bg-gradient-to-r from-gray-50 to-gray-100"
                        : index === 2
                        ? "bg-gradient-to-r from-amber-50 to-amber-100"
                        : ""
                    }`}>
                    <td className="py-3 px-4">{index === 0 ? "🥇" : index === 1 ? "🥈" : index === 2 ? "🥉" : <span className="text-gray-500 font-medium">{index + 1}</span>}</td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-3">
                        <div className="relative">
                          <img src={user.avatar} alt={user.name} className="w-10 h-10 rounded-full border-2 border-white shadow-sm" />
                        </div>
                        <div>
                          <span className="font-medium text-gray-700">{user.name}</span>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <span className="font-bold text-lg bg-gradient-to-r from-green-600 to-green-500 text-transparent bg-clip-text">{user.snake_record}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};

export default Snake;
